import { useCallback, useEffect, useRef, useState } from 'react';

// The Web Speech API isn't in TypeScript's DOM types everywhere (and Chrome/Safari prefix it), so describe
// just the parts we use.
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const speechSupported = () => recognitionCtor() !== null;

/**
 * Continuous speech-to-text that keeps going: browsers end a recognition session after silence or about a
 * minute, so it restarts itself until you press stop. Finished phrases go to `onFinal`.
 */
export function useSpeech(opts: { lang?: string; onFinal: (text: string) => void }) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const want = useRef(false);
  const rec = useRef<Recognition | null>(null);
  const failures = useRef(0);
  const onFinal = useRef(opts.onFinal);
  onFinal.current = opts.onFinal;
  const lang = opts.lang ?? 'en-GB';

  const begin = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor || !want.current) return;
    const r = new Ctor();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      failures.current = 0;
      let partial = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res[0].transcript;
        if (res.isFinal) {
          const clean = text.trim();
          if (clean) onFinal.current(clean);
        } else partial += text;
      }
      setInterim(partial);
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        want.current = false;
        setError('Microphone access is blocked – allow it for this site to transcribe.');
      } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
        failures.current++;
        if (failures.current >= 5) {
          want.current = false;
          setError(`Speech recognition keeps failing (${e.error}). Check your connection and try again.`);
        }
      }
    };
    r.onend = () => {
      setInterim('');
      if (want.current) {
        // Back off a little after errors so a flaky connection doesn't spin.
        window.setTimeout(begin, 250 + failures.current * 1000);
      } else {
        setListening(false);
      }
    };
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      /* already started – onend will retry */
    }
  }, [lang]);

  const start = useCallback(() => {
    setError(null);
    failures.current = 0;
    want.current = true;
    begin();
  }, [begin]);

  const stop = useCallback(() => {
    want.current = false;
    rec.current?.stop();
    setListening(false);
    setInterim('');
  }, []);

  useEffect(
    () => () => {
      want.current = false;
      rec.current?.abort();
    },
    [],
  );

  return { supported: recognitionCtor() !== null, listening, interim, error, start, stop };
}
